import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { spacing } from "@material-ui/system";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Paper,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { useWqData } from "./useWqData";
import WqMap from "./WqMap";
import WellSearch from "./WellSearch";
import ParameterChips from "./ParameterChips";
import WqDataTable from "./WqDataTable";
import WqRecordsDialog from "./WqRecordsDialog";
import WqEditDrawer from "./WqEditDrawer";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const SectionCard = styled(Paper)`
  padding: 16px 20px;
`;

const SectionHeader = styled(Box)`
  margin-bottom: 16px;
`;

const FiltersRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const MapFrame = styled(Box)`
  height: 520px;
  position: relative;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  overflow: hidden;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    height: 380px;
  }
`;

const ParamScroll = styled(Box)`
  flex: 1 1 auto;
`;

const WaterQualityViewer = () => {
  const { summary, parameters, stats, processed, isLoading, hasError } =
    useWqData();

  // --- Selection state ---
  const [selectedParameterNdx, setSelectedParameterNdx] = useState(null);
  const [selectedWellNdx, setSelectedWellNdx] = useState(null);

  const handleSelectWell = useCallback((wellNdx) => {
    // Toggle: clicking the already-selected well clears the selection.
    setSelectedWellNdx((prev) => (prev === wellNdx ? null : wellNdx));
  }, []);

  // Wrap the parameter setter so that if the new parameter doesn't apply to
  // the currently-selected well, we clear the selection in the same render —
  // avoids a flash of empty popup content.
  const handleParameterSelect = useCallback(
    (nextNdx) => {
      setSelectedParameterNdx(nextNdx);
      if (nextNdx == null || selectedWellNdx == null) return;
      const well = summary?.find((s) => s.well_ndx === selectedWellNdx);
      const stillApplies =
        well &&
        Array.isArray(well.parameters) &&
        well.parameters.includes(nextNdx);
      if (!stillApplies) setSelectedWellNdx(null);
    },
    [selectedWellNdx, summary]
  );

  // --- Map fly-to (search → map) ---
  const flyToRef = useRef(null);

  const registerFlyTo = useCallback((fn) => {
    flyToRef.current = fn;
  }, []);

  const handleSearchSelect = useCallback((well) => {
    setSelectedWellNdx(well.well_ndx);
    if (flyToRef.current) flyToRef.current(well.well_ndx);
  }, []);

  // --- Records dialog ---
  const [recordsDialog, setRecordsDialog] = useState({
    open: false,
    wellNdx: null,
    parameterNdx: null,
  });

  const handleShowRecords = useCallback((wellNdx, parameterNdx) => {
    setRecordsDialog({ open: true, wellNdx, parameterNdx });
  }, []);

  const closeRecordsDialog = useCallback(() => {
    setRecordsDialog((prev) => ({ ...prev, open: false }));
  }, []);

  // --- Edit drawer (soft-deletes / per-record edits) ---
  // Drawer state lives in the URL so it's deep-linkable. Anywhere in the
  // app can route to /reports-and-analysis/water-quality-viewer with the
  // ?wqEditWell, ?wqEditTs, ?wqEditParam query params and the drawer will
  // open with the right record pre-selected.
  const location = useLocation();
  const history = useHistory();

  const editDrawer = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const wellNdxRaw = sp.get("wqEditWell");
    const collectTimestamp = sp.get("wqEditTs");
    const parameterNdxRaw = sp.get("wqEditParam");
    if (!wellNdxRaw || !collectTimestamp) {
      return { open: false };
    }
    const wellNdx = Number(wellNdxRaw);
    const well = summary?.find((s) => s.well_ndx === wellNdx);
    return {
      open: true,
      wellNdx,
      wellName: well?.well_name || "",
      collectTimestamp,
      parameterNdx: parameterNdxRaw ? Number(parameterNdxRaw) : null,
    };
  }, [location.search, summary]);

  const handleEditEvent = useCallback(
    (row) => {
      // `row` is a wq_data_processed-shaped object: { well_ndx, well_name,
      // collect_timestamp, aqsci_parameter_ndx, ... }. Historical rows have
      // data_ndx >= 900_000_000 — those aren't editable (they live in
      // data_wq_historical, not de_wq).
      if (!row || row.data_ndx >= 900000000) return;
      const sp = new URLSearchParams(location.search);
      sp.set("wqEditWell", String(row.well_ndx));
      sp.set("wqEditTs", new Date(row.collect_timestamp).toISOString());
      if (row.aqsci_parameter_ndx != null) {
        sp.set("wqEditParam", String(row.aqsci_parameter_ndx));
      } else {
        sp.delete("wqEditParam");
      }
      history.replace({ search: sp.toString() });
    },
    [history, location.search]
  );

  const closeEditDrawer = useCallback(() => {
    const sp = new URLSearchParams(location.search);
    sp.delete("wqEditWell");
    sp.delete("wqEditTs");
    sp.delete("wqEditParam");
    history.replace({ search: sp.toString() });
  }, [history, location.search]);

  // --- Latest editable event per (well, parameter) ---
  // The popup's parameter panel needs to know whether the most recent
  // reading for this well + parameter came from the editable de_wq side
  // (data_ndx < 900_000_000) so it can offer a quick-edit pencil.
  const latestEditableEventByWellParam = useMemo(() => {
    const m = new Map();
    processed.forEach((r) => {
      if (r.data_ndx >= 900000000) return;
      const key = `${r.well_ndx}-${r.aqsci_parameter_ndx}`;
      const existing = m.get(key);
      if (
        !existing ||
        new Date(r.collect_timestamp) > new Date(existing.collect_timestamp)
      ) {
        m.set(key, r);
      }
    });
    return m;
  }, [processed]);

  // --- Derived values for the filter row caption ---
  const visibleSummaryWells = useMemo(() => {
    if (!selectedParameterNdx) return summary;
    return summary.filter((s) =>
      Array.isArray(s.parameters)
        ? s.parameters.includes(selectedParameterNdx)
        : false
    );
  }, [summary, selectedParameterNdx]);

  const wellCountText = useMemo(() => {
    if (!summary?.length) return "";
    if (!selectedParameterNdx) return `${summary.length} wells`;
    const param = parameters?.find(
      (p) => p.aqsci_parameter_ndx === selectedParameterNdx
    );
    const name = param?.parameter_name || param?.parameter_abbrev || "";
    return `${name} · ${visibleSummaryWells.length} of ${summary.length} wells`;
  }, [selectedParameterNdx, parameters, summary, visibleSummaryWells]);

  if (hasError) {
    return (
      <Alert severity="error">
        An error occurred loading water quality data. Please try again.
      </Alert>
    );
  }

  return (
    <>
      <Helmet title="Water Quality Viewer" />
      <Typography variant="h3" gutterBottom display="inline">
        Water Quality Viewer
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Water Quality Viewer</Typography>
      </Breadcrumbs>

      <Divider my={4} />

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <SectionCard>
            <SectionHeader>
              <Typography variant="h5" gutterBottom>
                Map
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ maxWidth: 820 }}
              >
                Explore water quality measurements across BSEACD wells. Click a
                well on the map to see every parameter recorded there, or use
                the table below to filter, sort, and export the underlying
                records.
              </Typography>
            </SectionHeader>

            <FiltersRow>
              <Box style={{ minWidth: 280 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Find a Well
                </Typography>
                <WellSearch wells={summary} onSelect={handleSearchSelect} />
              </Box>

              <ParamScroll>
                <ParameterChips
                  parameters={parameters}
                  selected={selectedParameterNdx}
                  onSelect={handleParameterSelect}
                  caption={wellCountText}
                />
              </ParamScroll>
            </FiltersRow>

            <MapFrame>
              <WqMap
                summary={summary}
                stats={stats}
                selectedParameterNdx={selectedParameterNdx}
                selectedWellNdx={selectedWellNdx}
                onSelectWell={handleSelectWell}
                onParameterChange={handleParameterSelect}
                onShowRecords={handleShowRecords}
                onEditEvent={handleEditEvent}
                latestEditableEventByWellParam={latestEditableEventByWellParam}
                registerFlyTo={registerFlyTo}
              />
            </MapFrame>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard>
            <SectionHeader>
              <Typography variant="h5" gutterBottom>
                All Measurement Records
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ maxWidth: 820 }}
              >
                Every individual reading underlying the map. Use the filter row
                under each column header to narrow results, click a header to
                sort, and use the export menu in the table's top-right to
                download a CSV.
              </Typography>
            </SectionHeader>
            <WqDataTable
              rows={processed}
              isLoading={isLoading}
              onEditEvent={handleEditEvent}
            />
          </SectionCard>
        </Grid>
      </Grid>

      <WqRecordsDialog
        open={recordsDialog.open}
        onClose={closeRecordsDialog}
        wellNdx={recordsDialog.wellNdx}
        parameterNdx={recordsDialog.parameterNdx}
        summary={summary}
        parameters={parameters}
        processed={processed}
        onEditEvent={handleEditEvent}
      />

      <WqEditDrawer
        open={editDrawer.open}
        onClose={closeEditDrawer}
        wellNdx={editDrawer.wellNdx}
        wellName={editDrawer.wellName}
        collectTimestamp={editDrawer.collectTimestamp}
        initialParameterNdx={editDrawer.parameterNdx}
        processed={processed}
      />
    </>
  );
};

export default WaterQualityViewer;
